import React from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Lock as LockIcon, Upgrade as UpgradeIcon, Code as CodeIcon } from '@mui/icons-material';
import { hasFeatureAccess, getUpgradeMessage } from '../utils/featureAccess';
import { getSubscriptionInfo } from '../utils/subscription';

/**
 * ProtectedPage Component
 * Shows upgrade prompt for users who don't have access to a feature
 */
const ProtectedPage = ({ feature, children }) => {
  const navigate = useNavigate();
  const hasAccess = hasFeatureAccess(feature);
  const subscriptionInfo = getSubscriptionInfo();
  const currentTier = subscriptionInfo?.subscription_tier || 'free';

  if (hasAccess) {
    return children;
  }

  // Special handling for internal plan - show development message
  if (currentTier === 'internal') {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CodeIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Development Feature
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This feature is currently in development. As an internal plan user, you should have access to all features including beta ones.
            If you're seeing this message, there may be a configuration issue or the feature is not yet implemented.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </Button>
            <Button 
              variant="contained" 
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Show upgrade prompt for regular users
  const upgradeMessage = getUpgradeMessage(feature);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <LockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Feature Not Available
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {upgradeMessage}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Current plan: <strong>{currentTier}</strong>
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button 
            variant="contained" 
            startIcon={<UpgradeIcon />}
            onClick={() => navigate('/pricing')}
          >
            View Plans
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ProtectedPage; 